import {JSX} from "wfr/commons/jsx"
import {ENavLanguages, EStage, JDownloadProductMetas, JLocInfos, JProductInfosSucc, L10nLabels} from "../../commons/api"
import {formatDate} from "../../commons/commons";
import {DownloadProductComponent, IVersionSelector, productInfos, urltree} from "./product";
import {DownloadMessageBox} from "./product-message-box";
import {ELevel, JMessage} from "../../commons/messages";
import {Dict} from "wfr/commons/common"
import {lang} from "../../commons/language-selector";

const labels:L10nLabels = {
	[ENavLanguages.fr]:{
		openVersions:"Voir les autres versions",
		closeVersions:"Fermer le menu des versions",
		otherVersions:"Autres versions",
		openOthersVersions:"Voir les autres versions",
		closeOthersVersions:"Fermer le menu des autres versions",
		choose:"Choisir",
		releaseNote:"note de version",
		newMinor:(lnk:string, releaseNote:string)=>{
			return `Une nouvelle version mineure est disponible : ${lnk}. Cette mise à jour est fortement recommandée, elle corrige des anomalies sans rupture de compatibilité${releaseNote?` (Voir la ${releaseNote} pour plus d'information)`:""}.`},
		newMajor:(lnk:string, releaseNote:string)=>{
			return `Une nouvelle version majeure est disponible : ${lnk}.${releaseNote? ` Voir la ${releaseNote} pour plus d'information.`:""}`}
	},
	[ENavLanguages.en]:{
		openVersions:"See other versions",
		closeVersions:"Close versions menu",
		otherVersions:"Other versions",
		openOthersVersions:"See other versions",
		closeOthersVersions:"Close other versions menu",
		choose:"Choose",
		releaseNote:"release note",
		newMinor:(lnk:string, releaseNote?:string)=>{
			return `A new minor version is available: ${lnk}. This bugfix update is strongly recommended.${releaseNote?` (See ${releaseNote}) for more information.`:""}.`},
		newMajor:(lnk:string, releaseNote?:string)=>{
			return `A new major version is available: ${lnk}.${releaseNote? ` See ${releaseNote} for more information.`:""}`}
	}
}




export class VersionSelector extends DownloadProductComponent implements IVersionSelector{
	protected versions:Dict<JDownloadProductMetas[]> = {}
	protected current:JDownloadProductMetas
	protected labels = labels

	loadData() {
		super.loadData();
		this.current = productInfos.metas
		productInfos.hist.forEach(hist=>{
			if(hist.unlisted) return;
			const major = hist.metas.majorVersion
			if(!(major in this.versions)) this.versions[major] = []
			this.versions[major].push(hist.metas)
		})
		const handleDownloadSucc = (succ:JProductInfosSucc<JDownloadProductMetas>)=>{
			if(!succ.unlisted){
				const major = succ.metas.majorVersion
				if(!(major in this.versions)) this.versions[major] = []
				this.versions[major].unshift(succ.metas)
			}
			succ.succ.forEach(handleDownloadSucc);
		}
		handleDownloadSucc(productInfos as JProductInfosSucc<JDownloadProductMetas>)
	}

	async connectedCallback(){
		await super.connectedCallback()

		const vs = this;

		const keys = (event:KeyboardEvent)=>{
			const button = vs.shadowRoot.querySelector("button")
			if(!button || button.classList.contains("closed")) return

			const selected = vs.shadowRoot.querySelector(".selected")
			const list = Array.from(vs.shadowRoot.querySelectorAll("ul.majorVersions :not([hidden]) a, ul.majorVersions :not([hidden]) button"))
			let idx = list.indexOf(selected)

			switch (event.code){
			case "ArrowDown":
				if(selected){
					selected.classList.remove("selected");
					if(++idx < list.length) list[idx].classList.add("selected")
				}
				else list.shift()?.classList.add("selected");
				event.stopPropagation()
				event.preventDefault()
				break
			case "ArrowUp":
				if(selected){
					selected.classList.remove("selected");
					if(--idx >= 0) list[idx].classList.add("selected")
				}
				else list.pop()?.classList.add("selected")
				event.stopPropagation()
				event.preventDefault()
				break
			case "Enter":
				if(selected) (selected as HTMLButtonElement|HTMLAnchorElement).click()
				else button.click()
				event.stopPropagation()
				event.preventDefault()
				break
			case "Escape":
				button.click()
				event.stopPropagation()
				event.preventDefault()
				break
			}
		}

		window.addEventListener("keyup", keys)
		window.addEventListener("click", e=>this.close(e))
	}

	rebuild(){
		if(!super.rebuild()) return false;
		this.shadowRoot.append(<button class="closed" title={this.s("openVersions")} onclick={this.open}>{this.fullVersion(this.current)}</button>)
		return true;
	}

	protected open(event:MouseEvent){
		const vs = (this.getRootNode() as ShadowRoot).host as VersionSelector
		this.classList.remove("closed")
		this.classList.add("opened")
		this.onclick = vs.close;
		this.setAttribute("title", vs.s("closeVersions"));
		vs.shadowRoot.append(<ul class="majorVersions">
			{Object.keys(vs.versions).sort((a,b)=>{return parseFloat(a)>parseFloat(b)?-1:1;}).map(major=>{
				const minors = vs.versions[major]
				const lastMinor = minors[0]
				const selected = major == vs.current.majorVersion && lastMinor.minorVersion == vs.current.minorVersion
				const opened = !selected && major == vs.current.majorVersion
				return <li class="majorVersion">
					{selected?<div class="selectedVersion">
						<div>{vs.majorVersion(lastMinor)}</div>
						<div>{vs.fullVersion(lastMinor)}</div>
					</div>: <a href={urltree+lastMinor.path}>
						<div>{vs.majorVersion(lastMinor)}</div>
						<div>{vs.fullVersion(lastMinor)}</div>
					</a>}
					{minors.length > 1 ? <div class="otherVersions">
						<button class={`otherVersions ${opened?"close":"open"}`} title={vs.s(opened?"closeOthersVersions":"openOthersVersions")} onclick={vs.toggleOtherVersions}><span>{vs.s("otherVersions")}</span></button>
						<ul class="minorVersions" hidden={!opened}>
							{minors.map((minor, i)=>{
								if(i == 0) return null;
								const minorSelected = opened && minor.minorVersion == vs.current.minorVersion
								return <li class="minorVersion">
									{minorSelected?<span class="selectedVersion">
										{vs.fullVersion(minor)}
										</span>:<a href={urltree+minor.path}>
										{vs.fullVersion(minor)}
										<span class="label">{vs.s("choose")}</span>
									</a>}
								</li>
							})}
						</ul>
					</div>:null}
				</li>
			})}
		</ul>)
		event.stopPropagation()
		event.preventDefault()
	}

	protected close(event:Event){
		const vs = (this.getRootNode() as ShadowRoot).host as VersionSelector || this
		vs.shadowRoot.querySelector("ul")?.remove()
		const button = vs.shadowRoot.querySelector("button.opened") as HTMLButtonElement
		if(button) {
			button.classList.remove("opened")
			button.classList.add("closed")
			button.onclick = vs.open;
			button.setAttribute("title", vs.s("openVersions"))
		}
	}

	protected toggleOtherVersions(event:Event){
		if(this.classList.contains("open")){
			this.classList.remove("open")
			this.classList.add("close")
			this.setAttribute("title", "closeOthersVersions")
			this.nextElementSibling.removeAttribute("hidden")
		}
		else{
			this.classList.remove("close")
			this.classList.add("open")
			this.setAttribute("title", "openOthersVersions")
			this.nextElementSibling.setAttribute("hidden", "true")
		}
		event.stopPropagation()
		event.preventDefault()
	}

	getVersionWarnBox(){
		const majorStr = Object.keys(this.versions).sort((a,b)=>{return parseFloat(a) > parseFloat(b) ? 1 : -1;}).pop();
		const major = majorStr == this.current.majorVersion ? null : this.versions[majorStr][0]
		const minor = this.versions[this.current.majorVersion][0].minorVersion != this.current.minorVersion ? this.versions[this.current.majorVersion][0] : null;

		if(minor == null && major == null) return null;
		const message:JMessage = {level:ELevel.warning,locInfos:{[lang]:{body:""}} as unknown as JLocInfos}
		if(minor){
			const lnk = `<a class="minorUpdate" href="${urltree}${minor.path}"><span>${minor.downloadLocInfos[lang].title} ${minor.majorVersion}.${minor.minorVersion} ${minor.stage!=EStage.final?minor.stage:""}</span></a>`
			const releaseNote = minor.releaseNote ? `<a class="releasenote" href="${minor.releaseNote}" target="_blank"><span>${this.s("releaseNote")}</span></a>`:null
			message.locInfos[lang].body += `<p>${this.s("newMinor", lnk, releaseNote)}</p>`
		}
		if(major){
			const lnk = `<a class="majorUpdate" href="${urltree}${major.path}"><span>${major.downloadLocInfos[lang].title} ${major.majorVersion}</span></a>`
			const releaseNote = major.releaseNote ? `<a class="releasenote" href="${major.releaseNote}" target="_blank"><span>${this.s("releaseNote")}</span></a>`:null
			message.locInfos[lang].body += `<p>${this.s("newMajor", lnk, releaseNote)}</p>`
		}
		return new DownloadMessageBox(message);
	}

	protected fullVersion(metas:JDownloadProductMetas) {
		return <span class="fullVersion">v{metas.majorVersion}.{metas.minorVersion} {metas.stage != EStage.final ? <span class={`stage ${metas.stage}`}>{metas.stage}</span> : ""} - {formatDate(metas.releaseDate)}</span>
	}

	protected majorVersion(metas: JDownloadProductMetas) {
		return <span class="majorVersion">{metas.downloadLocInfos[lang].title} {metas.majorVersion}</span>
	}
}

customElements.define('version-selector', VersionSelector)
